import { createAsyncThunk } from "@reduxjs/toolkit";
import { map, pickBy, size } from "lodash-es";
import { Dispatch } from "react";

import { IStoreState } from "App/types";
import { IDataKeys } from "shared/types";
import { VenueService, IJSONAPIVenueCollection } from "Venues/services";
import {
  IVenue,
  VENUE_ACTIONS,
  IVenuePatchData,
  IVenueFilters,
  ISuggestEdit,
  ISortingOptionKeys,
  IVenuePostData,
} from "Venues/types";
import { logout } from "Auth/state";
import { API_ENDPOINTS } from "shared/constants";
import { apiSortingOptions } from "Venues/utils";
import { CancelToken } from "axios";

const getAllVenues = (
  venueList: IJSONAPIVenueCollection,
  dispatch: Dispatch<any>,
  cancelToken?: CancelToken
): Promise<unknown> =>
  Promise.all(
    map(
      venueList.venue,
      ({ attributes }) =>
        attributes?.id && dispatchGetVenueAction(dispatch, attributes.id, cancelToken)
    )
  );

export const dispatchGetSearchVenues = (
  dispatch: Dispatch<any>,
  {
    filters,
    page,
    sort,
    band,
  }: {
    filters?: IVenueFilters;
    page?: number;
    sort?: ISortingOptionKeys;
    band?: string;
  },
  cancelToken?: CancelToken
) => {
  const defaultPage = page || 1;
  const apiSortObject = sort && apiSortingOptions[sort];
  const sortObject = apiSortObject
    ? { [`sort[${apiSortObject.field}]`]: apiSortObject.direction }
    : {};
  dispatch(
    getSearchVenues({
      page: defaultPage,
      filters: {
        ...filters,
        ...sortObject,
        page: defaultPage,
        band,
      },
      paginationKey: VENUE_ACTIONS.getSearchVenues,
      cancelToken,
    })
  );
};

export const getSearchVenues = createAsyncThunk(
  VENUE_ACTIONS.getSearchVenues,
  async (
    {
      filters,
      cancelToken,
    }: {
      paginationKey: string;
      filters: { [key: string]: any };
      page: number;
      cancelToken?: CancelToken;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const cleanedFilters: { [key: string]: any } = pickBy(
        filters,
        val => val !== null && val !== undefined && val !== ""
      );

      const data = await VenueService.searchVenues(
        {
          ...cleanedFilters,
          itemsPerPage: 30,
        },
        cancelToken
      );

      getAllVenues(data, dispatch, cancelToken);

      return {
        [IDataKeys.searchVenues]: data.venue,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetUserVenues = (dispatch: Dispatch<any>, page?: number) => {
  dispatch(
    getUserVenues({
      page: page || 1,
      paginationKey: VENUE_ACTIONS.getUserVenues,
    })
  );
};

export const getUserVenues = createAsyncThunk(
  VENUE_ACTIONS.getUserVenues,
  async (
    { page }: { page: number; paginationKey: string },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const user = (getState() as IStoreState).auth.currentUser?.id;

      // nest try catch so user does not logout when getVenues call returns 401
      try {
        if (user) {
          const data = await VenueService.getVenues({
            user,
            page,
            "sort[updatedAt]": "desc",
            itemsPerPage: 15,
          });

          getAllVenues(data, dispatch);

          return {
            [IDataKeys.userVenues]: data.venue,
            meta: data.meta,
            pagination: data.pagination,
          };
        }
      } catch (error) {
        return rejectWithValue(error);
      }
    } catch (error) {
      // if user token is not valid
      // user is probably deleted on another device
      if ((error as any).status === 401) {
        dispatch(logout());
      }

      return rejectWithValue(error);
    }
  }
);

export const dispatchGetVenueAction = (
  dispatch: Dispatch<any>,
  venueId: string,
  cancelToken?: CancelToken
) => {
  const action = API_ENDPOINTS.VENUE(venueId);
  return dispatch(getVenue({ venueId, action, cancelToken }));
};

export const getVenue = createAsyncThunk(
  VENUE_ACTIONS.getVenue,
  async (
    { venueId, cancelToken }: { venueId: string; action: string; cancelToken?: CancelToken },
    { rejectWithValue }
  ) => {
    try {
      const venue = await VenueService.getVenue(venueId, cancelToken);

      return venue;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createVenue = createAsyncThunk(
  VENUE_ACTIONS.createVenue,
  async ({ venue, users }: { venue: IVenuePostData; users?: string[] }, { rejectWithValue }) => {
    try {
      const createdVenue = await VenueService.createVenue(venue);
      const id = Object.values(createdVenue?.venue)?.[0]?.attributes?.id;

      if (id && users && size(users)) await VenueService.inviteEditors(id, users);

      return { id };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editVenue = createAsyncThunk(
  VENUE_ACTIONS.editVenue,
  async (
    { venueId, venueData }: { venueId: string; venueData: IVenuePatchData },
    { rejectWithValue }
  ) => {
    try {
      return await VenueService.editVenue(venueId, venueData);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteVenue = createAsyncThunk(
  VENUE_ACTIONS.deleteVenue,
  async (venueId: string, { rejectWithValue }) => {
    try {
      await VenueService.deleteVenue(venueId);
      return { path: API_ENDPOINTS.VENUE(venueId), dataKey: IDataKeys.venue };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editVenueStageOrder = createAsyncThunk(
  VENUE_ACTIONS.editVenueStageOrder,
  async (
    { venueId, sortedIdList }: { venueId: string; sortedIdList: string[] },
    { rejectWithValue }
  ) => {
    try {
      return await VenueService.editVenueStageOrder(sortedIdList, venueId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editVenueBands = createAsyncThunk(
  VENUE_ACTIONS.editVenueBands,
  async ({ venueId, bands }: { venueId: string; bands: string[] | [] }, { rejectWithValue }) => {
    try {
      return await VenueService.editVenueBands(venueId, bands);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const publishVenue = createAsyncThunk(
  VENUE_ACTIONS.publishVenue,
  async (venueId: string, { rejectWithValue }) => {
    try {
      await VenueService.publishVenue(venueId);
      return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const requestPublishVenue = createAsyncThunk(
  VENUE_ACTIONS.requestPublishVenue,
  async (venueId: string, { rejectWithValue }) => {
    try {
      await VenueService.requestPublishVenue(venueId);
      return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const unpublishVenue = createAsyncThunk(
  VENUE_ACTIONS.unpublishVenue,
  async (venueId: string, { rejectWithValue }) => {
    try {
      await VenueService.unpublishVenue(venueId);
      return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const inviteEditor = createAsyncThunk(
  VENUE_ACTIONS.inviteEditor,
  async ({ venueId, email }: { venueId: string; email: string }, { rejectWithValue }) => {
    try {
      return await VenueService.inviteEditor(venueId, email);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const inviteEditors = createAsyncThunk(
  VENUE_ACTIONS.inviteEditors,
  async ({ venueId, bandUsers }: { venueId: string; bandUsers: string[] }, { rejectWithValue }) => {
    try {
      return await VenueService.inviteEditors(venueId, bandUsers);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getEditorInvite = createAsyncThunk(
  VENUE_ACTIONS.getEditorInvite,
  async (id: string, { rejectWithValue }) => {
    try {
      return await VenueService.getEditorInvite(id);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const revokeEditorInvite = createAsyncThunk(
  VENUE_ACTIONS.revokeEditorInvite,
  async ({ inviteId }: { inviteId: string; venueId: string }, { rejectWithValue }) => {
    try {
      return await VenueService.revokeEditorInvite(inviteId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const unlinkEditor = createAsyncThunk(
  VENUE_ACTIONS.unlinkEditor,
  async ({ venue, userId }: { venue: IVenue; userId: string }, { rejectWithValue }) => {
    try {
      const users = venue.users?.reduce((users, { id }) => {
        if (id !== userId) {
          return [...users, API_ENDPOINTS.USER(id)];
        }
        return users;
      }, [] as string[]);

      return await VenueService.editVenue(venue.id, { users });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const isOnlyVenueUser = createAsyncThunk(
  VENUE_ACTIONS.isOnlyVenueUser,
  async (_, { rejectWithValue, getState }) => {
    try {
      const user = (getState() as IStoreState).auth.currentUser?.id;

      if (user) {
        const response = await VenueService.getVenues({
          user,
          singleUser: 1,
        });

        if (size(response.venue) !== 0) {
          return rejectWithValue("User is only venue user for a venue");
        }

        return;
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const suggestEdit = createAsyncThunk(
  VENUE_ACTIONS.suggestEdit,
  async ({ data, venueId }: { data: ISuggestEdit; venueId: string }, { rejectWithValue }) => {
    try {
      await VenueService.suggestEdit(data, venueId);
      return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
